/* ─────────────────────────────────────────────────────────────
   Shared chrome for the instructor Academy screens — the courses
   tab (InstructorCoursesTab) and the per-course details page
   (InstructorCourseDetails) render the same class rows, section
   headings, stat tiles and skeletons, so they live here once.
   ───────────────────────────────────────────────────────────── */
import { useEffect } from 'react'
import { createPortal } from 'react-dom'
import { appUrl } from '@astro/config'

/** Education app's host flow — starts the class (if needed) and joins the
 * VideoSDK room as the instructor. Course-agnostic, so standalone classes work. */
export const hostHref = (id) => appUrl('education', `/live-classes/${id}/host`)

/* A class belongs to the RECORDED section once its recording exists — or is
   on the way (flagged is_recorded and the session already ended). Everything
   else is a live session the instructor still hosts/hosted. */
export const isRecordedRow = (c) =>
  Boolean(c.recordingUrl) || (c.isRecorded && (c.rawStatus.includes('complete') || c.rawStatus.includes('end')))

export const isScheduledRow = (c) => c.rawStatus.includes('schedul') || c.rawStatus.includes('upcoming')

export function ClassSkeletonCard() {
  return (
    <div className="border border-stone-100 rounded-2xl p-4 animate-pulse" aria-hidden="true">
      <div className="flex gap-3 items-start">
        <div className="w-14 h-14 rounded-xl bg-stone-100 shrink-0" />
        <div className="flex-1 min-w-0 space-y-2">
          <div className="h-3.5 w-3/4 bg-stone-100 rounded-full" />
          <div className="h-2.5 w-1/2 bg-stone-100 rounded-full" />
        </div>
      </div>
      <div className="flex gap-3 mt-3">
        {[...Array(3)].map((_, i) => (
          <div key={i} className="h-2.5 w-14 bg-stone-100 rounded-full" />
        ))}
      </div>
    </div>
  )
}

export function SkeletonGrid({ count = 2 }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      {Array.from({ length: count }, (_, i) => <ClassSkeletonCard key={i} />)}
    </div>
  )
}

/** Section heading — eyebrow + title + a count badge, shared by all sections. */
export function SectionHead({ eyebrow, eyebrowClass, title, count, children }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5">
      {/* min-w-0 so the title can wrap instead of squeezing the controls beside it */}
      <div className="min-w-0">
        <span className={`eyebrow ${eyebrowClass}`}>{eyebrow}</span>
        <h3 className="text-xl text-astroDark font-royal flex items-center gap-2">
          {title}
          {count != null && (
            <span className="num text-[11px] font-bold text-stone-400 bg-stone-100 rounded-full px-2 py-0.5">{count}</span>
          )}
        </h3>
      </div>
      {children}
    </div>
  )
}

/** One at-a-glance count in a header's stat strip. */
export function StatTile({ icon, label, value, accent }) {
  return (
    <div className="flex-1 min-w-[104px] bg-white/70 border border-stone-100 rounded-xl px-4 py-3">
      <div className="flex items-center gap-1.5">
        <span className="text-sm">{icon}</span>
        <span className="text-[9px] font-bold uppercase tracking-wider text-stone-400">{label}</span>
      </div>
      <p className={`num text-2xl font-bold mt-1 leading-none ${accent || 'text-astroDark'}`}>{value}</p>
    </div>
  )
}

/** Category/level/type badge — the pill row above a course title. */
export function Badge({ className = 'bg-[#F5E6D3] text-[#A67C00]', children }) {
  return (
    <span className={`text-[9px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full ${className}`}>
      {children}
    </span>
  )
}

/** Small course meta chip — icon + label (price, updated date, visibility…). */
export function MetaChip({ icon, children }) {
  return (
    <span className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wide text-stone-500 bg-stone-50 border border-stone-100 rounded-full px-3 py-1">
      <span>{icon}</span>
      {children}
    </span>
  )
}

/** Instructor avatars + names row shared by the course card and hero.
 * `instructors` is [{ id, name, photo, designation? }]. */
export function InstructorsRow({ instructors, showDesignation = false }) {
  if (!instructors?.length) return null
  return (
    <div className="flex flex-wrap items-center gap-3 mt-2">
      {instructors.map((inst, i) => (
        <span key={inst.id ?? i} className="inline-flex items-center gap-2">
          {inst.photo ? (
            <img src={inst.photo} alt="" className="w-6 h-6 rounded-full object-cover" />
          ) : (
            <span className="w-6 h-6 rounded-full bg-[#3D0A0A] text-[#D4A017] text-[9px] font-bold flex items-center justify-center">
              {String(inst.name || '?')[0]}
            </span>
          )}
          <span className="text-xs font-semibold text-stone-600">{inst.name}</span>
          {showDesignation && inst.designation && <span className="text-[10px] text-stone-400">· {inst.designation}</span>}
        </span>
      ))}
    </div>
  )
}

/** Small calendar affordance shared by the class rows/cards — opens the
 * reschedule dialog. Only rendered for scheduled sessions (gated at the call
 * sites with isScheduledRow); muted until hovered, like the delete button. */
export function RescheduleClassButton({ c, onReschedule, className = '' }) {
  return (
    <button
      type="button"
      onClick={() => onReschedule(c)}
      title="Reschedule class"
      aria-label={`Reschedule ${c.title}`}
      className={`shrink-0 w-7 h-7 rounded-lg border border-stone-100 text-stone-300 hover:text-amber-600 hover:border-amber-200 hover:bg-amber-50 transition-colors text-xs flex items-center justify-center ${className}`}
    >
      🗓
    </button>
  )
}

/** Small trash-can affordance shared by the class rows/cards — muted until
 * hovered so it doesn't compete with the primary Host/Watch action. */
export function DeleteClassButton({ c, onDelete, className = '' }) {
  return (
    <button
      type="button"
      onClick={() => onDelete(c)}
      title="Delete class"
      aria-label={`Delete ${c.title}`}
      className={`shrink-0 w-7 h-7 rounded-lg border border-stone-100 text-stone-300 hover:text-red-500 hover:border-red-200 hover:bg-red-50 transition-colors text-xs flex items-center justify-center ${className}`}
    >
      🗑
    </button>
  )
}

/** Delete-a-live-class confirmation dialog — same portal/backdrop idiom as
 * the modals, red accent. `c` is the normalised class row being deleted. */
export function ConfirmDeleteClassModal({ c, busy, error, onConfirm, onClose }) {
  useEffect(() => {
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    const onKey = (e) => { if (e.key === 'Escape' && !busy) onClose() }
    window.addEventListener('keydown', onKey)
    return () => {
      document.body.style.overflow = prev
      window.removeEventListener('keydown', onKey)
    }
  }, [onClose, busy])

  const recorded = isRecordedRow(c)
  const modal = (
    <div className="fixed inset-0 z-[1200] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-[#3D0A0A]/60 backdrop-blur-sm" onClick={busy ? undefined : onClose} aria-hidden="true" />
      <div role="alertdialog" aria-modal="true" aria-label="Delete live class" className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
        <div className="w-12 h-12 rounded-2xl bg-red-50 text-red-500 text-xl flex items-center justify-center mb-4">🗑</div>
        <h3 className="text-xl text-astroDark font-royal">Delete this class?</h3>
        <p className="text-sm text-stone-500 mt-2">
          &ldquo;{c.title}&rdquo;{c.dateLabel && c.dateLabel !== '—' ? ` (${c.dateLabel})` : ''} will be permanently
          removed{recorded ? ' along with its recording link' : ' — enrolled students lose this session'}.
          {c.isLive ? ' It is ON AIR right now — participants will be cut off.' : ''} This cannot be undone.
        </p>
        {error && (
          <div className="mt-4 px-4 py-3 rounded-xl text-xs font-semibold bg-red-50 text-red-600 border border-red-100">
            {error}
          </div>
        )}
        <div className="flex items-center justify-end gap-2 mt-6 pt-4 border-t border-stone-100">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="border border-stone-200 text-stone-700 font-bold text-[10px] uppercase tracking-[0.15em] px-5 py-2.5 rounded-xl hover:bg-stone-50 transition-all disabled:opacity-60"
          >
            Keep Class
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={busy}
            className="bg-red-600 text-white font-bold text-[10px] uppercase tracking-[0.15em] px-5 py-2.5 rounded-xl hover:bg-red-700 transition-all disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {busy ? 'Deleting…' : 'Yes, Delete Class'}
          </button>
        </div>
      </div>
    </div>
  )
  return typeof document === 'undefined' ? null : createPortal(modal, document.body)
}

/** Compact one-line class row — date/recording chip, title, status, and the
 * right action (Host for live/scheduled, Watch for recordings). Pass
 * `onDelete` to append the delete affordance, `onReschedule` to append the
 * reschedule affordance (shown for scheduled sessions only). */
export function ClassRow({ c, onDelete, onReschedule }) {
  const recorded = isRecordedRow(c)
  const ready = Boolean(c.recordingUrl)
  return (
    <div className="flex items-center gap-3 py-2.5 px-2">
      <div className={`w-11 h-11 rounded-lg flex flex-col items-center justify-center shrink-0 text-white ${c.isLive ? 'bg-[#3D0A0A]' : recorded ? 'bg-stone-400' : 'bg-[#D48806]'}`}>
        {recorded ? (
          <span className="text-base">🎥</span>
        ) : (
          <>
            <span className="text-sm font-bold leading-none">{c.day}</span>
            <span className="text-[7px] uppercase tracking-widest opacity-80 mt-0.5">{c.mon}</span>
          </>
        )}
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold text-astroDark truncate">{c.title}</p>
        <p className="text-[10px] text-stone-400 uppercase tracking-wide font-bold mt-0.5 truncate">
          🗓 {c.dateLabel}{c.time ? ` · 🕐 ${c.time}` : ''}{c.dur ? ` · ${c.dur}` : ''}
        </p>
      </div>
      <span className={`hidden sm:inline-flex items-center gap-1.5 text-[9px] font-bold uppercase tracking-wide px-2 py-1 rounded-full shrink-0 ${recorded ? (ready ? 'bg-green-50 text-green-600' : 'bg-amber-50 text-amber-600') : c.cls}`}>
        {c.isLive && <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />}
        {recorded ? (ready ? 'Recorded' : 'Processing') : c.status}
      </span>
      <div className="shrink-0 w-16 text-right">
        {recorded ? (
          ready ? (
            <a href={c.recordingUrl} target="_blank" rel="noreferrer" className="text-[10px] font-bold uppercase tracking-wider text-goldAccent hover:underline">
              Watch →
            </a>
          ) : null
        ) : c.id != null && (c.isLive || isScheduledRow(c)) ? (
          <a href={hostHref(c.id)} className={`text-[10px] font-bold uppercase tracking-wider hover:underline ${c.isLive ? 'text-red-600' : 'text-goldAccent'}`}>
            {c.isLive ? '● Host →' : 'Host →'}
          </a>
        ) : null}
      </div>
      {onReschedule && c.id != null && !recorded && isScheduledRow(c) && (
        <RescheduleClassButton c={c} onReschedule={onReschedule} />
      )}
      {onDelete && c.id != null && <DeleteClassButton c={c} onDelete={onDelete} />}
    </div>
  )
}
